import { useCallback, useMemo, useRef } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { DateRangePicker, SingleSelectDropdown, type DateRangeValue } from "nazare-ui/components";

import { useDocumentTitle } from "../use-document-title";
import { useSecondaryHeader } from "../secondary-header-context";
import { useInboxAnalytics, type InboxAnalyticsInbox } from "../inbox-analytics-api";
import { buildInboxAnalyticsTableBuckets, buildInboxAnalyticsTableRows } from "../inbox-analytics-table";
import { InboxAnalyticsTable } from "../inbox-analytics-table-view";
import { DATE_RANGE_PRESETS } from "../date-range-presets.ts";
import { GRANULARITIES, readInboxAnalyticsUrlState, updateInboxAnalyticsUrl, type InboxAnalyticsGranularity, type InboxAnalyticsPeriod, type InboxAnalyticsSort } from "../inbox-analytics-url-state";
import { TableSection } from "../table-section";
import styles from "./inboxes.module.css";

const GRANULARITY_OPTIONS = GRANULARITIES.map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) }));
function dateFromUrl(value: string): Date { return new Date(`${value}T00:00:00`); }
function dateToUrl(value: Date | undefined): string | undefined { if (!value || Number.isNaN(value.getTime())) return undefined; return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`; }

export function InboxAnalyticsPage() {
  useDocumentTitle("Inbox analytics");
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readInboxAnalyticsUrlState(params), [params]);
  const query = useInboxAnalytics(filters);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const inboxes: InboxAnalyticsInbox[] = query.data?.inboxes ?? [];
  const update = useCallback((next: Partial<typeof filters>) => setParams((current) => updateInboxAnalyticsUrl(current, next)), [setParams]);
  const range = useMemo<DateRangeValue>(() => ({ from: dateFromUrl(filters.from), to: dateFromUrl(filters.to) }), [filters.from, filters.to]);
  const tableBuckets = useMemo(() => buildInboxAnalyticsTableBuckets(filters), [filters.from, filters.to, filters.granularity]);
  const tableRows = useMemo(() => buildInboxAnalyticsTableRows(inboxes, tableBuckets, filters.sort, filters.direction), [inboxes, tableBuckets, filters.sort, filters.direction]);
  const sortBy = useCallback((sortKey: InboxAnalyticsSort) => update({ sort: sortKey, direction: filters.sort === sortKey && filters.direction === "asc" ? "desc" : "asc" }), [filters.direction, filters.sort, update]);
  const selectedPreset = DATE_RANGE_PRESETS.find((preset) => preset.value === filters.period);
  const secondaryHeader = useMemo(() => (
    <div className={styles.secondaryHeader}>
      <nav aria-label="Inbox pages"><Link className={styles.pageTitle} to={`/inboxes${location.search}`}>Inbox analytics</Link></nav>
      <div className={styles.controls}>
        <DateRangePicker
          value={range}
          label="Filter inbox analytics by date"
          valueLabel={selectedPreset?.label}
          presets={DATE_RANGE_PRESETS}
          selectedPreset={selectedPreset?.value}
          onPresetChange={(period) => {
            update({ period: period as InboxAnalyticsPeriod });
          }}
          onChange={(next) => {
            const from = dateToUrl(next?.from);
            const to = dateToUrl(next?.to);
            if (from && to) update({ period: "custom", from, to });
          }}
        />
        <SingleSelectDropdown label="Choose table granularity" triggerLabel={filters.granularity[0].toUpperCase() + filters.granularity.slice(1)} options={GRANULARITY_OPTIONS} selectedValue={filters.granularity} onValueChange={(value) => update({ granularity: value as InboxAnalyticsGranularity })} />
      </div>
    </div>
  ), [filters, location.search, range, selectedPreset, update]);
  useSecondaryHeader(secondaryHeader);

  return <main className={styles.page}>
    <TableSection
      title="Inbox message comparison"
      count={query.data && !query.isError ? inboxes.length : undefined}
      shadowZIndex={3}
      tableWrapRef={tableScrollRef}
      tableWrapAriaLabel="Inbox message comparison by bucket"
      tableWrapTabIndex={0}
    >
      {query.isError ? <p className={styles.error}>Unable to load inbox analytics.</p> : query.isLoading ? <p className={styles.empty}>Loading inbox analytics…</p> : inboxes.length === 0 ? <p className={styles.empty}>No inbox analytics for this date range.</p> : <InboxAnalyticsTable rows={tableRows} buckets={tableBuckets} sort={filters.sort} direction={filters.direction} onSort={sortBy} scrollContainerRef={tableScrollRef} />}
    </TableSection>
  </main>;
}
